import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { useIntl } from 'react-intl';

const UPDATE_USER_MUTATION = gql`
  mutation UpdateCurrentUser($motivation: String, $transition_goals: [String!]) {
    updateCurrentUser(motivation: $motivation, transition_goals: $transition_goals)
  }
`;

const GOAL_KEY_MAP = {
  'Buying': 'buying',
  'Selling': 'selling',
  'Selling my practice': 'selling_my_practice',
  'Selling part of my practice': 'selling_part_of_my_practice',
  'Find a Successor': 'find_a_successor',
  'Become a Successor': 'become_a_successor',
  'Merging': 'merging',
  'Partnering': 'partnering',
  'Forming a Partnership': 'forming_a_partnership',
  'Finding a Junior Partner': 'finding_a_junior_partner',
  'Becoming a Junior Partner': 'becoming_a_junior_partner',
  'Business Continuity': 'business_continuity',
  'Mentoring': 'mentoring',
  'Being Mentored': 'being_mentored',
  'Looking for Successor': 'looking_for_successor',
  'Currently not interested in Buying / Selling': 'currentlynotinterestedin_buying/selling',
};

const TransitionGoalsEditModal = ({ user, options, onClose, onSave, onError }) => {
  const intl = useIntl();

  const translateGoal = (goal) => {
    const key = GOAL_KEY_MAP[goal];
    if (!key) return goal;
    return intl.formatMessage({ id: `transition_goals.${key}`, defaultMessage: goal });
  };
  const [step, setStep] = useState('motivation');
  const [motivation, setMotivation] = useState(user.motivation || null);
  const [selectedGoals, setSelectedGoals] = useState(user.transition_goals || []);

  const MOTIVATION_OPTIONS = [
    {
      key: 'growth',
      label: intl.formatMessage({ id: 'succession.profile_edit.modals.transition_goals_edit.grow_my_business_label' }),
      subtitle: intl.formatMessage({ id: 'succession.profile_edit.modals.transition_goals_edit.grow_my_business_subtitle' }),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ),
    },
    {
      key: 'succession',
      label: intl.formatMessage({ id: 'succession.profile_edit.modals.transition_goals_edit.plan_my_succession_label' }),
      subtitle: intl.formatMessage({ id: 'succession.profile_edit.modals.transition_goals_edit.plan_my_succession_subtitle' }),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
    },
    {
      key: 'no_motivation',
      label: intl.formatMessage({ id: 'succession.profile_edit.modals.transition_goals_edit.just_browsing_label' }),
      subtitle: intl.formatMessage({ id: 'succession.profile_edit.modals.transition_goals_edit.just_browsing_subtitle' }),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
        </svg>
      ),
    },
  ];

  const [updateUser, { loading }] = useMutation(UPDATE_USER_MUTATION, {
    onCompleted: () => {
      onSave(intl.formatMessage({ id: 'succession.profile_edit.modals.transition_goals_edit.success_message' }));
      handleClose();
    },
    onError: (error) => onError(error),
    refetchQueries: ['GetCurrentUserProfile'],
    awaitRefetchQueries: true,
  });

  const handleMotivationSelect = (newMotivation) => {
    // Clear goals when motivation changes
    if (newMotivation !== motivation) {
      setSelectedGoals([]);
    }
    setMotivation(newMotivation);

    // Automatically proceed to next step or save
    if (newMotivation === 'no_motivation') {
      // Skip goals step and save immediately for browsing
      updateUser({
        variables: {
          motivation: null,
          transition_goals: []
        }
      });
    } else {
      // Automatically go to goals selection
      setStep('goals');
    }
  };

  const handleNext = () => {
    if (step === 'motivation' && motivation) {
      if (motivation === 'no_motivation') {
        // Skip goals step for browsing
        handleSave([]);
      } else {
        setStep('goals');
      }
    }
  };

  const handleBack = () => {
    setStep('motivation');
  };

  const handleSave = (goals = selectedGoals) => {
    updateUser({
      variables: {
        motivation: motivation === 'no_motivation' ? null : motivation,
        transition_goals: goals
      }
    });
  };

  const handleClose = () => {
    onClose();
    // Reset to first step after a delay
    setTimeout(() => {
      setStep('motivation');
    }, 300);
  };

  const toggleGoal = (goal) => {
    if (selectedGoals.includes(goal)) {
      setSelectedGoals(selectedGoals.filter((g) => g !== goal));
    } else {
      setSelectedGoals([...selectedGoals, goal]);
    }
  };

  // Get available goals based on motivation
  let availableGoals = [];
  if (motivation === 'succession') {
    availableGoals = options.successionTransitionGoals;
  } else if (motivation === 'growth') {
    availableGoals = options.growthTransitionGoals;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" onClick={handleClose}>
      <div className="bg-succession-bg-secondary rounded-lg max-w-4xl w-full border border-succession-border overflow-hidden flex flex-col" style={{ maxHeight: '85vh' }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-2xl font-bold text-succession-text-primary">
              {step === 'motivation'
                ? intl.formatMessage({ id: 'succession.profile_edit.modals.transition_goals_edit.motivation_step_title' })
                : intl.formatMessage({ id: 'succession.profile_edit.modals.transition_goals_edit.goals_step_title' })}
            </h2>
            <button onClick={handleClose} className="p-2 hover:bg-succession-bg-primary rounded-full transition-colors">
              <svg className="w-6 h-6 text-succession-text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-succession-text-muted text-base">
            {step === 'motivation'
              ? intl.formatMessage({ id: 'succession.profile_edit.modals.transition_goals_edit.motivation_step_subtitle' })
              : intl.formatMessage({ id: 'succession.profile_edit.modals.transition_goals_edit.goals_step_subtitle' })}
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {step === 'motivation' ? (
            <div className="space-y-3">
              {MOTIVATION_OPTIONS.map((opt) => {
                const selected = motivation === opt.key;
                return (
                  <div
                    key={opt.key}
                    onClick={() => handleMotivationSelect(opt.key)}
                    className={`relative p-4 rounded-lg border cursor-pointer transition-all duration-200 ${
                      selected
                        ? 'border-succession-primary bg-succession-primary/5 shadow-md'
                        : 'border-succession-border bg-succession-bg-secondary hover:border-succession-text-muted hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                        selected ? 'bg-succession-primary/10 text-succession-primary' : 'bg-succession-bg-primary text-succession-text-muted'
                      }`}>
                        {opt.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-base mb-1 text-succession-text-primary">{opt.label}</h3>
                        <p className="text-sm text-succession-text-muted">{opt.subtitle}</p>
                      </div>
                      <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 transition-all duration-200 ${
                        selected ? 'border-succession-primary bg-succession-primary' : 'border-succession-border'
                      }`}>
                        {selected && (
                          <div className="w-full h-full rounded-full bg-succession-primary flex items-center justify-center">
                            <div className="w-2 h-2 rounded-full bg-white" />
                          </div>
                        )}
                      </div>
                    </div>
                    {selected && (
                      <div className="absolute inset-0 rounded-lg ring-2 ring-succession-primary pointer-events-none" />
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <>
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
                {availableGoals.map((goal) => {
                  const checked = selectedGoals.includes(goal);
                  return (
                    <div
                      key={goal}
                      onClick={() => toggleGoal(goal)}
                      className={`relative p-5 rounded-xl border cursor-pointer transition-all duration-200 ${
                        checked
                          ? 'border-succession-primary bg-succession-primary/5 shadow-md'
                          : 'border-succession-border bg-succession-bg-secondary hover:border-succession-text-muted hover:shadow-md'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-[15px] mb-1 text-succession-text-primary">{translateGoal(goal)}</h3>
                        </div>
                        <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 transition-all duration-200 mt-0.5 ${
                          checked ? 'border-succession-primary bg-succession-primary' : 'border-succession-border'
                        }`}>
                          {checked && (
                            <div className="w-full h-full rounded-full bg-succession-primary flex items-center justify-center">
                              <div className="w-2 h-2 rounded-full bg-white" />
                            </div>
                          )}
                        </div>
                      </div>
                      {checked && (
                        <div className="pointer-events-none absolute inset-0 rounded-xl ring-2 ring-succession-primary" />
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-center text-sm text-succession-text-muted mt-4">
                {intl.formatMessage({ id: 'succession.profile_edit.modals.transition_goals_edit.you_can_select_multiple' })}
              </p>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex justify-between flex-shrink-0 border-t border-succession-border pt-4">
          {step === 'goals' ? (
            <>
              <button onClick={handleBack} className="px-4 py-2 border border-succession-border rounded-lg text-succession-text-primary hover:bg-succession-bg-primary transition-colors">
                {intl.formatMessage({ id: 'succession.profile_edit.common.back' })}
              </button>
              <button
                onClick={() => handleSave()}
                disabled={loading || selectedGoals.length < 1}
                className={`px-4 py-2 rounded-lg text-white transition-colors ${
                  loading || selectedGoals.length < 1 ? 'bg-succession-primary opacity-50 cursor-not-allowed' : 'bg-succession-primary hover:opacity-90'
                }`}
              >
                {loading ? intl.formatMessage({ id: 'succession.profile_edit.common.saving' }) : intl.formatMessage({ id: 'succession.profile_edit.common.save' })}
              </button>
            </>
          ) : (
            <>
              <button onClick={handleClose} className="px-4 py-2 border border-succession-border rounded-lg text-succession-text-primary hover:bg-succession-bg-primary transition-colors">
                {intl.formatMessage({ id: 'succession.profile_edit.common.cancel' })}
              </button>
              <button
                onClick={handleNext}
                disabled={!motivation}
                className={`px-4 py-2 rounded-lg text-white transition-colors ${
                  !motivation ? 'bg-succession-primary opacity-50 cursor-not-allowed' : 'bg-succession-primary hover:opacity-90'
                }`}
              >
                {intl.formatMessage({ id: 'succession.profile_edit.common.next' })}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

TransitionGoalsEditModal.propTypes = {
  user: PropTypes.object.isRequired,
  options: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

export default TransitionGoalsEditModal;
